"use client"
import React, { useEffect, useRef } from "react"
import { styled } from "styled-components"
import { black, white, accentYellow as yellow, red, darkBlueBorder } from "../components/colors"

const Input = styled.input<{ $transparent?: boolean }>`
  background-color: ${props =>
    props.$transparent ? "transparent" : darkBlueBorder};
  color: ${white};
  padding: 16px 8px;
  border-color: ${white};
  border-style: solid;
  border-width: 2px;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  font-weight: bold;
  caret-color: ${yellow};

  &::placeholder {
    color: ${white};
    font-weight: 100;
    text-transform: lowercase;
    opacity: 0.5;
  }

  &:focus,
  &:focus-visible {
    outline-color: ${yellow};
    border-color: ${yellow};
  }
  &:invalid {
    border-color: ${red};
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > label {
    color: ${black};
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    background-color: ${yellow};
    width: fit-content;
    border-radius: 4px;
    padding: 2px 8px;
    position: relative;
    left: 16px;
    top: 10px;
  }
`
const ErrorMessage = styled.div`
  text-align: left;
  color: ${white};
  background-color: ${red};
  padding: 0px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  align-self: flex-start;
  margin-top: 4px;
  margin-left: 16px;
  text-transform: uppercase;
`

export const TextInput: React.FC<{
  allowClear?: boolean
  placeholder?: string
  type?: string
  disabled?: boolean
  onChange: (value: string) => void
  onValidityChange?: (validity: boolean) => void
  value?: string
  label?: string
  className?: string
  required?: boolean
  error?: string
  transparent?: boolean
  readonly?: boolean
  autocomplete?: "on" | "off"
  name?: string
  maxLength?: number
}> = ({
  type = "text",
  label,
  value = "",
  className = "",
  placeholder = "",
  required = false,
  onChange,
  onValidityChange,
  error,
  disabled,
  readonly,
  transparent = true,
  autocomplete,
  name,
  maxLength
}) => {
  const [touched, setTouched] = React.useState(false)
  const [internalErrorMessage, setInternalErrorMessage] = React.useState<
    string | null
  >(null)
  const inputName = `input-for-${label}`
  const innerRef = useRef<any>()

  const handleInputEvent = () => {
    setTouched(true)
    setInternalErrorMessage(null)
  }

  const handleFocusOut = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target
    const value = input.value
    const isValid = input.checkValidity()
    onValidityChange?.(isValid)
    if (isValid) {
      return
    }
    if (required && (!value || value.length === 0)) {
      onValidityChange?.(false)
      return setInternalErrorMessage("This field cannot be left blank!")

    }
    if (type === "email") {
      return setInternalErrorMessage("Email is not valid!")
    }
  }

  useEffect(() => {
    if (innerRef && innerRef.current) {
      const isValid = innerRef.current?.checkValidity() || false
      if (required && (!value || value.length === 0)) {
        onValidityChange?.(false)
        return
      }
      onValidityChange?.(isValid)
    }
  }, [value])

  return (
    <InputContainer className={className}>
      {label && <label htmlFor={inputName}>{label}</label>}
      <Input
        ref={innerRef}
        type={type}
        name={inputName || name}
        value={value}
        autoComplete={autocomplete}
        onChange={event => {
          return onChange(event.target.value as string)
        }}
        placeholder={placeholder}
        required={required && touched}
        disabled={disabled}
        maxLength={maxLength}
        readOnly={readonly}
        onInput={handleInputEvent}
        onBlur={handleFocusOut}
        $transparent={transparent}
      />
      {(error || internalErrorMessage) && (
        <ErrorMessage>{error || internalErrorMessage}</ErrorMessage>
      )}
    </InputContainer>
  )
}
